import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { B2bContractServiceOrder } from '../../../models/Contract';
import { ContractsService } from '../../../services/contracts.service';
import { ErrorService } from '../../../shared/services/error.service';
import { TableAction } from '../../../shared/components/table/table.component';
import { exportB2bTableToExcel } from '../../../utils/b2b-table-export.util';

@Component({
  selector: 'app-b2b-service-orders',
  templateUrl: './b2b-service-orders.component.html',
  styleUrls: ['../shared/b2b-operational-shared.css', './b2b-service-orders.component.css']
})
export class B2bServiceOrdersComponent implements OnInit {
  contractId: number | null = null;
  contractNumber = '';
  contractName = '';
  items: B2bContractServiceOrder[] = [];
  isLoading = false;
  showDetail = false;
  detailLoading = false;
  selected: B2bContractServiceOrder | null = null;
  statusFilter = '';

  columns = [
    { titulo: 'OS', label: 'orderNumber' },
    { titulo: 'Placa', label: 'plate' },
    { titulo: 'Abertura', label: 'openingDate', formatter: (v: any) => this.formatDate(v) },
    { titulo: 'Status', label: 'status' },
    { titulo: 'Agendada', label: 'isScheduled', formatter: (v: any) => (v ? 'Sim' : 'Não') },
    { titulo: 'Serviços', label: 'servicesCount' },
    { titulo: 'Total', label: 'totalServices', formatter: (v: any) => this.formatMoney(v), sensitive: true },
    { titulo: 'Oficina', label: 'openingReason' }
  ];

  actions: TableAction[] = [
    { label: 'Detalhes', icon: 'fas fa-eye', action: 'detail', class: 'btn-view' },
    { label: 'Abrir OS', icon: 'fas fa-external-link-alt', action: 'open', class: 'btn-edit' }
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private contractsService: ContractsService,
    private errorService: ErrorService
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.contractId = +params['id'];
      if (this.contractId) {
        this.loadContractInfo();
        this.loadItems();
      }
    });
  }

  loadContractInfo(): void {
    if (!this.contractId) return;
    this.contractsService.getB2bContractById(this.contractId).subscribe({
      next: (c: any) => {
        this.contractNumber = c?.contractNumber || c?.ContractNumber || '';
        this.contractName = c?.contractName || c?.ContractName || '';
      },
      error: () => {}
    });
  }

  loadItems(): void {
    if (!this.contractId) return;
    this.isLoading = true;
    const status = this.statusFilter || undefined;
    this.contractsService.getB2bServiceOrders(this.contractId, status).subscribe({
      next: (list) => {
        this.items = (list || []).map(x => this.normalize(x));
        this.isLoading = false;
      },
      error: (err) => {
        this.errorService.handleHttpError(err);
        this.isLoading = false;
      }
    });
  }

  onStatusFilterChange(): void {
    this.loadItems();
  }

  onActionClick(event: { action: string; item: any }): void {
    if (event.action === 'detail') {
      this.openDetail(event.item);
    } else if (event.action === 'open') {
      this.openServiceOrder(event.item);
    }
  }

  openDetail(item: B2bContractServiceOrder): void {
    if (!this.contractId || !item?.id) return;
    this.showDetail = true;
    this.detailLoading = true;
    this.selected = item;
    this.contractsService.getB2bServiceOrderById(this.contractId, item.id).subscribe({
      next: (detail) => {
        this.selected = this.normalize(detail);
        this.detailLoading = false;
      },
      error: (err) => {
        this.errorService.handleHttpError(err);
        this.detailLoading = false;
      }
    });
  }

  closeDetail(): void {
    this.showDetail = false;
    this.selected = null;
  }

  openServiceOrder(item: B2bContractServiceOrder): void {
    if (!item?.id) return;
    this.router.navigate(['/service-orders', item.id, 'view']);
  }


  exportExcel(): void {
    exportB2bTableToExcel(
      `b2b-manutencoes-${this.contractNumber || this.contractId}`,
      this.columns,
      this.items,
      'Manutencoes'
    );
  }

  navigateBack(): void {
    if (this.contractId) this.router.navigate(['/b2b/contracts', this.contractId, 'view']);
    else this.router.navigate(['/b2b/contracts']);
  }

  formatDate(value: any): string {
    if (!value) return '—';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('pt-BR');
  }

  formatMoney(value: any): string {
    if (value == null || value === '') return '—';
    return Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }

  private normalize(raw: any): B2bContractServiceOrder {
    const services = (raw?.services || raw?.Services || []).map((s: any) => ({
      id: s.id ?? s.Id,
      description: s.description ?? s.Description ?? '',
      quantity: Number(s.quantity ?? s.Quantity ?? 0),
      unitValue: Number(s.unitValue ?? s.UnitValue ?? 0),
      total: Number(s.total ?? s.Total ?? 0)
    }));

    return {
      id: raw.id ?? raw.Id,
      orderNumber: raw.orderNumber ?? raw.OrderNumber ?? '',
      status: raw.status ?? raw.Status ?? '',
      openingDate: raw.openingDate ?? raw.OpeningDate ?? null,
      openingReason: raw.openingReason ?? raw.OpeningReason ?? null,
      description: raw.description ?? raw.Description ?? null,
      vehicleId: raw.vehicleId ?? raw.VehicleId ?? null,
      plate: raw.plate ?? raw.Plate ?? '—',
      b2bContractId: raw.b2bContractId ?? raw.B2BContractId,
      b2bContractItemId: raw.b2bContractItemId ?? raw.B2BContractItemId ?? null,
      entryDate: raw.entryDate ?? raw.EntryDate ?? null,
      startWorkDate: raw.startWorkDate ?? raw.StartWorkDate ?? null,
      deadlineDate: raw.deadlineDate ?? raw.DeadlineDate ?? null,
      endWorkDate: raw.endWorkDate ?? raw.EndWorkDate ?? null,
      pickupDate: raw.pickupDate ?? raw.PickupDate ?? null,
      isScheduled: !!(raw.isScheduled ?? raw.IsScheduled),
      openingMileage: raw.openingMileage ?? raw.OpeningMileage ?? null,
      fuelLevel: raw.fuelLevel ?? raw.FuelLevel ?? null,
      servicesCount: raw.servicesCount ?? raw.ServicesCount ?? services.length,
      totalServices: Number(raw.totalServices ?? raw.TotalServices ?? 0),
      services
    };
  }
}
